import { useState, useEffect, useContext } from 'react'
import { HospitalContext } from '../context/HospitalContext'

interface UseHospitalDataResult<T> {
    data: T | null
    loading: boolean
    initialLoading: boolean
    error: string | null
    refetch: () => void
}

export function useHospitalData<T = any>(
    endpoint: string,
    hospitalIds: string[]
): UseHospitalDataResult<T> {
    const { refreshKey } = useContext(HospitalContext)
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState(true)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [refetchTrigger, setRefetchTrigger] = useState(0)

    useEffect(() => {
        if (!hospitalIds || hospitalIds.length === 0) {
            setData(null)
            setLoading(false)
            setInitialLoading(false)
            setError(null)
            return
        }

        const abortController = new AbortController()

        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                const idsParam = hospitalIds.join(',')
                const url = `${endpoint}/${idsParam}`

                const response = await fetch(url, { signal: abortController.signal })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const jsonData = await response.json()
                setData(jsonData)
                setInitialLoading(false)
            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') return
                setError(err instanceof Error ? err.message : 'Failed to fetch data')
                setData(null)
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => abortController.abort()
    }, [endpoint, hospitalIds.join(','), refetchTrigger, refreshKey])

    const refetch = () => setRefetchTrigger((prev) => prev + 1)

    return { data, loading, initialLoading, error, refetch }
}
